import { useCallback, useMemo, type ReactNode } from "react"
import { useTranslation } from "react-i18next";
import { BookPlus, Download, FileInput, PlusCircle, RefreshCcwDot, UserCircle, type LucideIcon } from "lucide-react";
import AddEventFromTemplate, { type SelectedEvent } from "../../../components/AddEventFromTemplate";
import { useScheduleContext } from "../../../hooks/context/useScheduleContext";
import { useMenuContext } from "../../../hooks/context/useMenuContext";
import { useEventMenu } from "../../../hooks/grid/useContextMenuConfig/useEventMenu";
import { useUnassignedShiftsMenu } from "../../../hooks/grid/useContextMenuConfig/useUnassignedShiftsMenu";
import { useDayCellMenu } from "../../../hooks/grid/useContextMenuConfig/useDayCellMenu";
import { useOpenShiftMenu } from "../../../hooks/grid/useContextMenuConfig/useOpenShiftMenu";
import { usePermissions } from "../../../hooks/usePermissions";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import { useExportScheduleQuery } from "../../../api/useExportScheduleQuery";
import { usePreferences } from "../../../../../Settings/Configuration/hooks/usePreferences";
import { SCHEDULE_PREFERENCE_KEYS } from "../../../constants/scheduleTypes";

export type MenuItem = {
    icon: LucideIcon; // or LucideIcon if you want stricter typing
    label: string;
    onChange?: () => void;
    hidden?: boolean;
    disabled?: boolean;
    submenu?: {
        key: string;
        content: ReactNode;
    };
};

export type ScheduleMenus = {
    "cell-menu": MenuItem[];
    "event-menu": MenuItem[];
    "unassigned-menu": MenuItem[];
    "cell-menu-without-events": MenuItem[];
    "open-shift-menu": MenuItem[];
};

export const useMenuConfig = ({
    openActionsModal,
    openCopyModal,
    openDeleteModal,
    openAssignModal,
    openProfileSheet,
    openTransferModal,
    openShiftTradeDrawer,
    openClaimShiftDrawer,
    openSaveDayAsTemplate,
    openLoadTemplatesModal,
    openMoveToOpenShiftsModal
}: any) => {
    const { t } = useTranslation();
    const { dispatch, type, calendarEvents } = useScheduleContext();
    const { selected, resource, closeMenu } = useMenuContext();
    const [allFilters] = useFilters();
    const exportSchedule = useExportScheduleQuery();
    const hideUserProfile = usePreferences(`${SCHEDULE_PREFERENCE_KEYS[type]}.hideUserProfile`);
    // const canCreateRequest = useAccess(!isTeamScheduler && "request.canCreate");

    const { canCreate, canApprove, canCreateUnassignedShift } = usePermissions();

    const dayCellMenu = useDayCellMenu({
        openActionsModal,
        openCopyModal,
        openDeleteModal,
        openSaveDayAsTemplate,
        openLoadTemplatesModal
    });

    const eventMenu = useEventMenu({
        openActionsModal,
        openCopyModal,
        openDeleteModal,
        openTransferModal,
        // openAssignModal,
        openShiftTradeDrawer,
        openMoveToOpenShiftsModal
    });
    const unassignedShiftsMenu = useUnassignedShiftsMenu({
        openDeleteModal,
        openAssignModal
    });
    const openShiftsMenu = useOpenShiftMenu({ openClaimShiftDrawer });

    const onExport = useCallback(() => {
        const baseParams: any = {
            filters: allFilters?.filters ?? [],
            sort: allFilters?.sort,
            startDate: selected,
            endDate: selected,
        };

        exportSchedule.mutate(baseParams);
    }, [allFilters?.filters, allFilters?.sort, selected, exportSchedule]);

    return useMemo<ScheduleMenus>(() => {
        const createUnassignedEventItem = {
            icon: BookPlus,
            label: t("create-unassigned-event"),
            onChange: () => {
                dispatch({
                    type: "selected-cell-or-event",
                    payload: { date: selected, isCreating: true, isUnassignedShift: true }
                });
                closeMenu();
            },
            hidden: !canCreateUnassignedShift || type !== "team",
            disabled: false,
        };

        const createEventItem = {
            icon: PlusCircle,
            label: t("create"),
            onChange: () => {
                dispatch({
                    type: "selected-cell-or-event",
                    payload: { date: selected, isCreating: true, }
                });
                closeMenu();
            },
            hidden: !canCreate,
            disabled: false,
        };

        const profileItem = {
            icon: UserCircle,
            label: t("profile"),
            onChange: () => {
                openProfileSheet(resource)
                closeMenu();
            },
            hidden: type === "home" || hideUserProfile,
            disabled: false
        };

        const eventMenuWithProfile = [
            ...eventMenu.slice(0, 1),
            profileItem,
            ...eventMenu.slice(1),
        ].filter((item: MenuItem) => !item.hidden);

        const _dayCellMenu = [
            createEventItem,
            createUnassignedEventItem,
            ...dayCellMenu.slice(1),
            // {
            //     icon: Download,
            //     label: t("export"),
            //     onChange: () => onExport(),
            //     hidden: false,
            //     disabled: !(calendarEvents?.[selected]?.total > 0)
            // }
        ].filter((item: MenuItem) => !item.hidden);

        return {
            "cell-menu": _dayCellMenu,
            "event-menu": eventMenuWithProfile,
            "unassigned-menu": unassignedShiftsMenu.slice(1),
            "cell-menu-without-events": [
                createEventItem,
                createUnassignedEventItem,
                {
                    icon: FileInput,
                    label: t("add-from-template"),
                    hidden: ["template", "profile", "my-profile", "home"].includes(type) || !canCreate,
                    disabled: false,
                    submenu: {
                        key: "add-template",
                        content: (
                            <AddEventFromTemplate
                                key={selected?.id}
                                selected={{
                                    date: selected,
                                    startDate: selected
                                } as SelectedEvent}
                                closeMenu={closeMenu}
                            />
                        )
                    },
                },
                {
                    icon: RefreshCcwDot,
                    label: t("load-day-template"),
                    onChange: () => openLoadTemplatesModal({ date: selected, type: "day" }),
                    hidden: !canApprove || ["template", "profile", "my-profile", "home"].includes(type),
                    disabled: false,
                }
            ].filter((item: MenuItem) => !item.hidden),
            "open-shift-menu": openShiftsMenu
        }
    }, [
        dispatch,
        type,
        selected,
        canCreateUnassignedShift,
        canCreate,
        canApprove,
        eventMenu,
        resource,
        // calendarEvents,
        dayCellMenu,
        unassignedShiftsMenu,
        hideUserProfile,
        openProfileSheet,
        openLoadTemplatesModal,
        openShiftsMenu,
        closeMenu,
        // onExport,
        t
    ]);
}
